import {Component, inject} from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle
} from '@angular/material/dialog';
import {MatButton} from "@angular/material/button";
import {NgClass} from "@angular/common";
import {MatRadioModule} from '@angular/material/radio';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatChipsModule} from '@angular/material/chips';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ProductType} from '../../models/client-options.model';
import {SelectableEntity} from '../../models/selectable-entity.model';

type RefreshEntities = () => Promise<SelectableEntity[]> | SelectableEntity[];

// Define the shape of the data passed to the dialog
export interface ClientSelectionDialogData {
    entities: SelectableEntity[];
    title?: string;
    message?: string;
    entityType?: 'client' | 'account';
    refreshEntities?: RefreshEntities;
}

@Component({
    selector: 'app-client-selection-dialog',
    templateUrl: './client-selection-dialog.component.html',
    standalone: true,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatButton,
        NgClass,
        MatRadioModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatChipsModule,
        MatTooltipModule
    ],
    styleUrls: ['./client-selection-dialog.component.scss']
})
export class ClientSelectionDialogComponent {
    dialogRef = inject<MatDialogRef<ClientSelectionDialogComponent>>(MatDialogRef);
    data = inject<ClientSelectionDialogData>(MAT_DIALOG_DATA);

    title: string;
    message: string;
    entities: SelectableEntity[];
    filteredEntities: SelectableEntity[] = [];
    selectedEntityId: string | null = null;
    searchText: string = '';
    entityType: 'client' | 'account';
    isRefreshing = false;

    constructor() {
        const data = this.data;

        this.entityType = data.entityType || 'client';
        this.title = data.title || (this.entityType === 'client' ? 'Select Client' : 'Select Account');
        this.message = data.message || `Please select a ${this.entityType} to continue:`;

        this.setEntities(data.entities);
    }

    get canRefresh(): boolean {
        return typeof this.data.refreshEntities === 'function';
    }

    private setEntities(entities: SelectableEntity[]): void {
        const previousSelection = this.selectedEntityId;

        // Sort entities alphabetically by name or ID
        this.entities = [...entities].sort((a, b) => {
            if (this.entityType === 'client' && a.name && b.name) {
                return a.name.localeCompare(b.name);
            } else {
                return a.id.localeCompare(b.id);
            }
        });

        if (previousSelection && this.entities.some(entity => entity.id === previousSelection)) {
            this.selectedEntityId = previousSelection;
        } else {
            this.selectedEntityId = this.entities[0]?.id ?? null;
        }

        this.filterEntities();
    }

    /**
     * Filters the entities based on the search text
     */
    filterEntities(): void {
        if (!this.searchText) {
            this.filteredEntities = [...this.entities];
            return;
        }

        const searchLower = this.searchText.toLowerCase();
        this.filteredEntities = this.entities.filter(entity => {
            if (this.entityType === 'client' && entity.name) {
                return entity.name.toLowerCase().includes(searchLower);
            } else {
                return entity.id.toLowerCase().includes(searchLower);
            }
        });
    }

    /**
     * Clears the search text and resets the filtered entities
     */
    clearSearch(): void {
        this.searchText = '';
        this.filterEntities();
    }

    async refreshEntities(): Promise<void> {
        if (!this.data.refreshEntities || this.isRefreshing) {
            return;
        }

        this.isRefreshing = true;

        try {
            const refreshedEntities = await Promise.resolve(this.data.refreshEntities());
            this.setEntities(refreshedEntities);
        } catch (error) {
            console.error(`Failed to refresh ${this.entityType} list:`, error);
        } finally {
            this.isRefreshing = false;
        }
    }

    /**
     * Gets the display name for a product
     * @param product The product type
     * @returns The display name for the product
     */
    getProductName(product: string): string {
        switch (product) {
            case ProductType.ShineAI:
                return 'Shine AI';
            case ProductType.ShineConnect:
                return 'Shine Connect';
            case ProductType.ShineEnrich:
                return 'Shine Enrich';
            case ProductType.ShineWebinars:
                return 'Shine Webinars';
            case ProductType.ShineTranslations:
                return 'Shine Translations';
            default:
                return product;
        }
    }

    /**
     * Gets the CSS class for a product chip
     * @param product The product type
     * @returns The CSS class for the product chip
     */
    getProductClass(product: string): string {
        switch (product) {
            case ProductType.ShineAI:
                return 'shine-ai-chip';
            case ProductType.ShineConnect:
                return 'shine-connect-chip';
            case ProductType.ShineEnrich:
                return 'shine-enrich-chip';
            case ProductType.ShineWebinars:
                return 'shine-webinars-chip';
            case ProductType.ShineTranslations:
                return 'shine-translations-chip';
            default:
                return '';
        }
    }

    /**
     * Gets the CSS class for an access level chip
     * @param access The access level
     * @returns The CSS class for the access level chip
     */
    getAccessClass(access: string): string {
        switch (access?.toLowerCase()) {
            case 'admin':
                return 'admin-chip';
            case 'user':
                return 'user-chip';
            default:
                return '';
        }
    }

    onConfirm(): void {
        // Close the dialog, return the selected entity ID
        this.dialogRef.close(this.selectedEntityId);
    }

    onDismiss(): void {
        // Close the dialog without selecting an entity
        this.dialogRef.close(null);
    }
}
